import React from 'react';

// Schnee-Animation für das Header-Menü (Canvas-basiert)
export function MenuSnowEffect() {
  const canvasRef = React.useRef(null);
  const snowPileRef = React.useRef([]);
  const flakesRef = React.useRef([]);
  const animationRef = React.useRef(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const parent = canvas.parentElement;

    const resize = () => {
      canvas.width = parent.offsetWidth;
      canvas.height = parent.offsetHeight;
      if (snowPileRef.current.length === 0) {
        snowPileRef.current = new Array(Math.ceil(canvas.width)).fill(0).map((_, x) => {
          const wave1 = Math.sin(x * 0.02) * 8;
          const wave2 = Math.sin(x * 0.05 + 1) * 5;
          const wave3 = Math.sin(x * 0.1 + 2) * 3;
          return 66 + wave1 + wave2 + wave3 + Math.random() * 4;
        });
      }
    };

    resize();
    window.addEventListener('resize', resize);

    const flakeTypes = ['circle', 'circle', 'circle', 'star', 'crystal'];

    const drawFlake = (x, y, size, type, opacity) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.globalAlpha = opacity;

      if (type === 'circle') {
        ctx.beginPath();
        ctx.arc(0, 0, size, 0, Math.PI * 2);
        ctx.fillStyle = 'white';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(0, 0, size * 1.3, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.25)';
        ctx.fill();
      } else if (type === 'star') {
        ctx.strokeStyle = 'white';
        ctx.lineWidth = size * 0.3;
        ctx.lineCap = 'round';
        for (let i = 0; i < 6; i++) {
          ctx.rotate(Math.PI / 3);
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(0, size * 1.5);
          ctx.stroke();
        }
      } else if (type === 'crystal') {
        ctx.strokeStyle = 'white';
        ctx.lineWidth = size * 0.25;
        ctx.lineCap = 'round';
        for (let i = 0; i < 6; i++) {
          ctx.rotate(Math.PI / 3);
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(0, size * 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(0, size * 0.7);
          ctx.lineTo(size * 0.5, size * 1.1);
          ctx.moveTo(0, size * 0.7);
          ctx.lineTo(-size * 0.5, size * 1.1);
          ctx.moveTo(0, size * 1.3);
          ctx.lineTo(size * 0.35, size * 1.6);
          ctx.moveTo(0, size * 1.3);
          ctx.lineTo(-size * 0.35, size * 1.6);
          ctx.stroke();
        }
      }

      ctx.restore();
    };

    const createFlake = () => {
      const type = flakeTypes[Math.floor(Math.random() * flakeTypes.length)];
      const isSpecial = type !== 'circle';
      return {
        x: Math.random() * canvas.width,
        y: -10 - Math.random() * 80,
        size: isSpecial ? 3 + Math.random() * 5 : 1.5 + Math.random() * 4,
        speed: 0.3 + Math.random() * 0.8,
        swayPhase: Math.random() * Math.PI * 2,
        swaySpeed: 0.008 + Math.random() * 0.015,
        swayAmount: 8 + Math.random() * 15,
        opacity: 0.6 + Math.random() * 0.4,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.02,
        type
      };
    };

    for (let i = 0; i < 25; i++) {
      const flake = createFlake();
      flake.y = Math.random() * canvas.height * 0.7;
      flakesRef.current.push(flake);
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.beginPath();
      ctx.moveTo(0, canvas.height);
      for (let x = 0; x < canvas.width; x++) {
        const pileHeight = snowPileRef.current[x] || 66;
        ctx.lineTo(x, canvas.height - pileHeight);
      }
      ctx.lineTo(canvas.width, canvas.height);
      ctx.closePath();

      const pileGradient = ctx.createLinearGradient(0, canvas.height - 100, 0, canvas.height);
      pileGradient.addColorStop(0, 'rgba(255, 255, 255, 0.97)');
      pileGradient.addColorStop(0.3, 'rgba(245, 250, 255, 0.95)');
      pileGradient.addColorStop(0.7, 'rgba(235, 245, 255, 0.9)');
      pileGradient.addColorStop(1, 'rgba(225, 240, 255, 0.85)');
      ctx.fillStyle = pileGradient;
      ctx.fill();

      for (let i = 0; i < 15; i++) {
        const gx = Math.random() * canvas.width;
        const gy = canvas.height - (snowPileRef.current[Math.floor(gx)] || 66) + Math.random() * 10;
        ctx.beginPath();
        ctx.arc(gx, gy, 0.5 + Math.random(), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${0.3 + Math.random() * 0.5})`;
        ctx.fill();
      }

      flakesRef.current.forEach((flake, index) => {
        flake.y += flake.speed;
        flake.swayPhase += flake.swaySpeed;
        flake.rotation += flake.rotationSpeed;
        const sway = Math.sin(flake.swayPhase) * flake.swayAmount;
        const displayX = flake.x + sway;

        const floorX = Math.floor(Math.max(0, Math.min(canvas.width - 1, displayX)));
        const pileHeight = snowPileRef.current[floorX] || 66;
        const groundLevel = canvas.height - pileHeight;

        if (flake.y >= groundLevel - flake.size * 2) {
          const landX = floorX;
          const spreadRadius = 4 + Math.floor(Math.random() * 6);

          for (let dx = -spreadRadius; dx <= spreadRadius; dx++) {
            const px = landX + dx;
            if (px >= 0 && px < canvas.width) {
              const factor = 1 - Math.abs(dx) / (spreadRadius + 1);
              const addHeight = factor * (0.15 + Math.random() * 0.25);
              snowPileRef.current[px] = Math.min(110, (snowPileRef.current[px] || 66) + addHeight);
            }
          }

          flakesRef.current[index] = createFlake();
        } else {
          ctx.save();
          ctx.translate(displayX, flake.y);
          ctx.rotate(flake.rotation);
          ctx.translate(-displayX, -flake.y);
          drawFlake(displayX, flake.y, flake.size, flake.type, flake.opacity);
          ctx.restore();
        }
      });

      for (let x = 1; x < canvas.width - 1; x++) {
        const left = snowPileRef.current[x - 1] || 66;
        const center = snowPileRef.current[x] || 66;
        const right = snowPileRef.current[x + 1] || 66;
        snowPileRef.current[x] = Math.max(66, center * 0.9995 + (left + right) * 0.00025);
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1
      }}
    />
  );
}
